// CreditsContext — shares the live ledger balance across the shell.
//
// The spend-confirmation UX needs two things that outlive any single
// component:
//   1. AppShell's TopBar wants to PREFER the live ledger balance over the
//      plan-derived remainder when the ledger is enabled.
//   2. The Generate lanes want to REFRESH that balance after each
//      confirmed spend (so the header chip ticks down immediately) and
//      want to know whether the ledger is even enabled (so they can skip
//      the confirmation dialog when it isn't).
//
// Everything degrades gracefully: services.credits.balance() resolves to
// null on 404 / network error / older deployments, in which case
// ``balance`` stays null and ``ledgerEnabled`` stays false — callers fall
// back to today's behaviour (plan-derived chip, no confirmation gate).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { services } from "../../services";

interface CreditsContextValue {
  /** Live ledger balance as a number, or null when unavailable. */
  balance: number | null;
  /** True only when the BFF reported ledger_enabled. */
  ledgerEnabled: boolean;
  /** Re-fetch the balance (call after a confirmed spend). */
  refresh: () => void;
}

const CreditsContext = createContext<CreditsContextValue>({
  balance: null,
  ledgerEnabled: false,
  refresh: () => {},
});

export function CreditsProvider({ children }: { children: ReactNode }) {
  const [balance, setBalance] = useState<number | null>(null);
  const [ledgerEnabled, setLedgerEnabled] = useState(false);

  const refresh = useCallback(() => {
    let cancelled = false;
    void services.credits
      .balance()
      .then((b) => {
        if (cancelled) return;
        if (b && b.ledgerEnabled) {
          setLedgerEnabled(true);
          const n = Number(b.balance);
          setBalance(Number.isFinite(n) ? n : null);
        } else {
          // 404 / network / ledger disabled — stay degraded.
          setLedgerEnabled(false);
          setBalance(null);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setLedgerEnabled(false);
        setBalance(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const cleanup = refresh();
    return cleanup;
  }, [refresh]);

  const value = useMemo<CreditsContextValue>(
    () => ({ balance, ledgerEnabled, refresh }),
    [balance, ledgerEnabled, refresh],
  );

  return <CreditsContext.Provider value={value}>{children}</CreditsContext.Provider>;
}

export function useCredits(): CreditsContextValue {
  return useContext(CreditsContext);
}
